import React, { useState, useCallback, useRef } from 'react';
// import { useDropzone } from 'react-dropzone';
import { adminService } from '../services/adminApi';
import { Upload, File, X, CheckCircle, AlertCircle, Loader2, Music, Plus, Search } from 'lucide-react';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const FileUploader = () => {
  const currentYear = new Date().getFullYear();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [mode, setMode] = useState('select'); // 'select' or 'create'
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [songResults, setSongResults] = useState([]);
  const [searchingSongs, setSearchingSongs] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);

  // New song state
  const [newSong, setNewSong] = useState({
    title: '',
    collection: '',
    track_number: '',
    arranged_by: '',
    admin_notes: ''
  });

  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const acceptedFiles = Array.from(e.target.files).filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File ${file.name} exceeds the 100MB limit and will be skipped.`);
        return false;
      }
      return true;
    });
    setFiles(acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file),
      status: 'pending'
    })));
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const acceptedFiles = Array.from(e.dataTransfer.files).filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File ${file.name} exceeds the 100MB limit and will be skipped.`);
        return false;
      }
      return true;
    });
    setFiles(acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file),
      status: 'pending'
    })));
  }, []);

  const [isDragActive, setIsDragActive] = useState(false);

  // Simple mock of dropzone props
  const getRootProps = () => ({
    onDrop,
    onDragOver: (e) => { e.preventDefault(); setIsDragActive(true); },
    onDragLeave: () => setIsDragActive(false),
    onClick: () => fileInputRef.current?.click()
  });

  const getInputProps = () => ({
    type: 'file',
    multiple: true,
    style: { display: 'none' },
    ref: fileInputRef,
    onChange: handleFileChange,
    accept: '.pdf,.mp3,.wav,.m4a'
  });

  const handleSongSearch = async (val) => {
    setSearchQuery(val);
    if (val.length < 2) {
      setSongResults([]);
      return;
    }

    setSearchingSongs(true);
    try {
      const data = await adminService.searchSongs(val);
      setSongResults(data);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchingSongs(false);
    }
  };

  const selectSong = (song) => {
    setSelectedSong(song);
    setSearchQuery(song.title);
    setSongResults([]);
  };

  const uploadFiles = async () => {
    let targetSlug = selectedSong?.song_slug;

    if (mode === 'create') {
      if (!newSong.title) {
        alert("Song title is required.");
        return;
      }
      setUploading(true);
      try {
        const res = await adminService.createSong(newSong);
        targetSlug = res.song_slug;
      } catch (err) {
        console.error(err);
        alert("Failed to create song record.");
        setUploading(false);
        return;
      }
    }

    if (!targetSlug) {
      alert("Please select or create a target song first.");
      return;
    }

    setUploading(true);
    const newResults = [];

    for (const file of files) {
      try {
        // 1. Upload to B2 via Backend Proxy (No CORS issues)
        const uploadRes = await adminService.proxyUpload(file);
        
        // 2. Register media in DB
        const category = file.type.includes('pdf') ? 'score' : 'audio';
        
        await adminService.addMediaToSong(targetSlug, {
          category,
          type: file.type.split('/')[1],
          label: file.name,
          url: uploadRes.url,
          voice_part: null
        });

        newResults.push({ name: file.name, status: 'success' });
      } catch (err) {
        console.error(err);
        newResults.push({ name: file.name, status: 'error', message: err.message });
      }
    }

    setResults(newResults);
    setUploading(false);
    setFiles([]);
    if (mode === 'create') {
      setNewSong({ title: '', collection: '', arranged_by: '', admin_notes: '' });
      setMode('select');
    }
  };

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Upload new scores or songs</h2>
          <div className="flex bg-gray-100 p-1 rounded-xl">
            <button 
              onClick={() => { setMode('select'); setSelectedSong(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${mode === 'select' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <div className="flex items-center gap-2">
                <Search size={16} />
                Existing Song
              </div>
            </button>
            <button 
              onClick={() => setMode('create')}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${mode === 'create' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <div className="flex items-center gap-2">
                <Plus size={16} />
                New Song
              </div>
            </button>
          </div>
        </div>
        
        {/* Target Identification */}
        <div className="mb-8">
          {mode === 'select' ? (
            <div className="relative">
              <label className="block text-sm font-bold text-gray-700 mb-2">Search Repository</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  {searchingSongs ? <Loader2 className="animate-spin" size={18}/> : <Music size={18} />}
                </div>
                <input
                  type="text"
                  placeholder="Search by title, lyrics, or hymn number..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500"
                  value={searchQuery}
                  onChange={(e) => handleSongSearch(e.target.value)}
                />
              </div>

              {songResults.length > 0 && (
                <div className="absolute z-20 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-60 overflow-y-auto">
                  {songResults.map(song => (
                    <button
                      key={song.song_slug}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center justify-between border-b border-gray-50 last:border-0"
                      onClick={() => selectSong(song)}
                    >
                      <div>
                        <span className="font-bold text-gray-900">{song.title}</span>
                        <span className="ml-2 text-xs text-gray-500 uppercase bg-gray-100 px-2 py-0.5 rounded-full font-bold">{song.collection}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {selectedSong && (
                <div className="mt-3 flex items-center gap-2 text-emerald-600 bg-emerald-50 px-4 py-2 rounded-xl text-sm font-bold border border-emerald-100 animate-fade-in">
                  <CheckCircle size={16} />
                  Selected: {selectedSong.title}
                  <button onClick={() => setSelectedSong(null)} className="ml-auto text-emerald-400 hover:text-emerald-600 transition-colors">
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50/50 p-6 rounded-3xl border border-blue-100/50 animate-slide-up">
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Song Title *</label>
                <input
                  type="text"
                  placeholder="e.g. Amazing Grace"
                  className="w-full px-4 py-3 bg-white border-2 border-transparent rounded-xl focus:border-blue-500 transition-all outline-none shadow-sm"
                  value={newSong.title}
                  onChange={(e) => setNewSong({...newSong, title: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Collection</label>
                <input
                  type="text"
                  placeholder={`e.g. Christmas ${currentYear}`}
                  className="w-full px-4 py-3 bg-white border-2 border-transparent rounded-xl focus:border-blue-500 transition-all outline-none shadow-sm"
                  value={newSong.collection}
                  onChange={(e) => setNewSong({...newSong, collection: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Hymn / Track #</label>
                <input
                  type="number"
                  placeholder="e.g. 123"
                  className="w-full px-4 py-3 bg-white border-2 border-transparent rounded-xl focus:border-blue-500 transition-all outline-none shadow-sm"
                  value={newSong.track_number}
                  onChange={(e) => setNewSong({...newSong, track_number: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Arranged By</label>
                <input
                  type="text"
                  placeholder="Composer/Arranger"
                  className="w-full px-4 py-3 bg-white border-2 border-transparent rounded-xl focus:border-blue-500 transition-all outline-none shadow-sm"
                  value={newSong.arranged_by}
                  onChange={(e) => setNewSong({...newSong, arranged_by: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Admin Notes</label>
                <textarea
                  placeholder={`e.g. Youth Choir Easter ${currentYear}`}
                  className="w-full px-4 py-3 bg-white border-2 border-transparent rounded-xl focus:border-blue-500 transition-all outline-none shadow-sm h-24 resize-none"
                  value={newSong.admin_notes}
                  onChange={(e) => setNewSong({...newSong, admin_notes: e.target.value})}
                />
              </div>
            </div>
          )}
        </div>

        {/* Dropzone */}
        <div 
          {...getRootProps()} 
          className={`border-2 border-dashed rounded-3xl p-12 text-center transition-all duration-300 cursor-pointer ${
            isDragActive ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 'border-gray-200 hover:border-blue-400 hover:bg-gray-50'
          }`}
        >
          <input {...getInputProps()} />
          <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Upload className="text-blue-500" size={36} />
          </div>
          <p className="text-xl font-bold text-gray-900">
            {isDragActive ? "Drop files now" : "Select files for upload"}
          </p>
          <p className="text-sm text-gray-500 mt-2">PDF Scores or Audio Recordings (Max 100MB)</p>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="mt-8 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                Selected Files
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-bold">{files.length}</span>
              </h3>
              <button onClick={() => setFiles([])} className="text-xs font-bold text-gray-400 hover:text-red-500 transition-colors uppercase tracking-widest">
                Clear All
              </button>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {files.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 group hover:border-blue-200 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center group-hover:text-blue-600 transition-colors">
                      <File size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate max-w-[200px] md:max-w-md">{file.name}</p>
                      <p className="text-xs text-gray-500 font-medium">{(file.size / 1024 / 1024).toFixed(2)} MB • {file.type.split('/')[1].toUpperCase()}</p>
                    </div>
                  </div>
                  <button onClick={() => setFiles(files.filter((_, i) => i !== idx))} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>
            
            <button
              onClick={uploadFiles}
              disabled={uploading || (mode === 'select' && !selectedSong) || (mode === 'create' && !newSong.title)}
              className={`w-full py-4 rounded-2xl font-bold text-white transition-all shadow-xl mt-4 ${
                uploading || (mode === 'select' && !selectedSong) || (mode === 'create' && !newSong.title)
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' 
                  : 'bg-blue-600 hover:bg-blue-700 hover:-translate-y-1 shadow-blue-100 active:scale-95'
              } flex items-center justify-center gap-3 text-lg`}
            >
              {uploading ? (
                <>
                  <Loader2 className="animate-spin" size={24} />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Upload size={24} />
                  <span>{mode === 'create' ? 'Create Song & Upload' : 'Start Upload'}</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="mt-8 pt-8 border-t border-gray-100 space-y-3 animate-fade-in">
            <h3 className="font-bold text-gray-900">Summary</h3>
            {results.map((res, idx) => (
              <div key={idx} className={`p-4 rounded-2xl flex items-center gap-3 text-sm font-bold border transition-all ${
                res.status === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'
              }`}>
                <div className={`p-1.5 rounded-full ${res.status === 'success' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  {res.status === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                </div>
                <span className="truncate flex-1">{res.name}</span>
                <span className="opacity-70">{res.status === 'success' ? 'Stored in B2 & DB' : res.message}</span>
              </div>
            ))}
            <button onClick={() => setResults([])} className="w-full py-3 text-gray-400 text-xs font-bold hover:bg-gray-50 rounded-xl transition-all uppercase tracking-widest mt-2">
              Dismiss Notification
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploader;
